export const CHECKOUT_URL = 'https://aac-en.impultienda.ar/checkout/';

export const images = {
  hero: 'https://cdn.magicpatterns.com/uploads/vjT6NabjCxsJA3xunHmXpA/AAC-EN.png',
  author: 'https://cdn.magicpatterns.com/uploads/u9WqsUGSnWmFoDadUgNad8/Dr._Elena_Martins_-_AAC.png',
};

export const heroChecks = [
  'Practical planning frameworks',
  'Visual communication templates',
  'Functional observation tools',
  'Implementation checklists',
  'Communication partner resources',
];

export const beforeItems = [
  'You have difficulty organizing observations about an individual’s communication needs.',
  'You search through multiple files to find useful visual communication examples.',
  'You need a clearer framework for planning and documenting AAC supports.',
  'You want to involve families, educators, and other communication partners more effectively.',
  'You spend time recreating worksheets and planning documents from scratch.',
];

export const afterItems = [
  'Organize relevant communication observations using practical recording tools.',
  'Access visual examples and planning templates in one collection.',
  'Follow a structured educational framework for organizing implementation steps.',
  'Prepare materials to facilitate collaboration with communication partners.',
  'Document plans, observations, and adjustments more consistently.',
];

export const painPoints = [
  {
    title: 'You know AAC matters, but organizing the process feels overwhelming.',
    body: 'You understand the importance of supporting communication, but turning different considerations into an organized plan can be challenging.',
  },
  {
    title: 'You need practical tools, not just more theory.',
    body: 'You want examples, worksheets, checklists, and visual resources that help you organize your work.',
  },
  {
    title: 'You struggle to keep your documentation consistent.',
    body: 'Observations, planning notes, communication goals, and follow-up records can become scattered across different documents.',
  },
  {
    title: 'You want to create more useful visual supports.',
    body: 'You need a practical starting point for organizing vocabulary, visual schedules, communication boards, and everyday communication opportunities.',
  },
  {
    title: 'You want better collaboration with families and educators.',
    body: 'You recognize that communication support extends beyond a single professional session and requires attention to everyday interactions.',
  },
  {
    title: 'You want a clearer way to plan your next steps.',
    body: 'You don’t need a rigid formula for every individual. You need a structured reference that helps you organize relevant information and make thoughtful, individualized decisions.',
  },
];

export const volumes = [
  {
    number: 1,
    color: '#2563A8',
    title: 'AAC Foundations & Planning Framework',
    lead: 'Build a clearer foundation for organizing your AAC work.',
    body: 'Explore core AAC concepts, communication access, participation, and the role of individualized planning.',
    items: [
      'An overview of AAC terminology and fundamental concepts.',
      'A framework for organizing communication-related information.',
      'Planning worksheets for identifying questions that require further exploration.',
      'A guide to documenting priorities and next steps.',
    ],
  },
  {
    number: 2,
    color: '#12807F',
    title: 'Functional Communication Observation Guide',
    lead: 'Organize observations before planning communication supports.',
    body: 'Learn how to structure observations of communication opportunities, preferences, participation, and environmental barriers.',
    items: [
      'Functional observation worksheets.',
      'Context and activity recording templates.',
      'Questions for documenting communication opportunities.',
      'Tools for identifying information that may need further assessment.',
    ],
  },
  {
    number: 3,
    color: '#D9701A',
    title: 'AAC System Planning & Personalization',
    lead: 'Organize the considerations involved in planning individualized communication supports.',
    body: 'Explore factors relevant to selecting and personalizing AAC resources in collaboration with the individual and appropriate professionals.',
    items: [
      'AAC planning worksheets.',
      'Vocabulary organization frameworks.',
      'Visual accessibility considerations.',
      'Templates for documenting personalization decisions.',
    ],
  },
  {
    number: 4,
    color: '#7A4CC2',
    title: 'Visual Communication Resource Design',
    lead: 'Create a more organized approach to developing visual supports.',
    body: 'Explore the planning of communication boards, visual schedules, choice boards, and other visual resources for different activities.',
    items: [
      'Visual resource planning templates.',
      'Everyday activity examples.',
      'Vocabulary organization worksheets.',
      'Adaptation checklists for different environments.',
    ],
  },
  {
    number: 5,
    color: '#2F8F4E',
    title: 'Communication Partner Collaboration',
    lead: 'Plan how communication partners can support everyday communication opportunities.',
    body: 'Explore practical ways to organize collaboration among families, educators, caregivers, and relevant professionals.',
    items: [
      'Communication partner discussion guides.',
      'Collaborative planning worksheets.',
      'Everyday interaction examples.',
      'Tools for documenting agreed-upon support strategies.',
    ],
  },
  {
    number: 6,
    color: '#C2415B',
    title: 'AAC Implementation & Follow-Up Planner',
    lead: 'Keep implementation plans, observations, and adjustments organized.',
    body: 'Explore ways to document how communication supports are used, identify potential barriers, and organize follow-up discussions.',
    items: [
      'Implementation planning checklists.',
      'Follow-up documentation templates.',
      'Participation observation records.',
      'Worksheets for organizing adjustments and future planning.',
    ],
  },
];

export const tools = [
  {
    icon: 'checklist',
    title: 'AAC Planning Checklist',
    body: 'A structured checklist for organizing key considerations before implementing communication supports.',
  },
  {
    icon: 'observation',
    title: 'Functional Observation Worksheets',
    body: 'Templates for recording relevant observations across activities and environments.',
  },
  {
    icon: 'visual',
    title: 'Visual Support Planning Templates',
    body: 'Organizers for planning visual schedules, choice boards, and communication resources.',
  },
  {
    icon: 'partner',
    title: 'Communication Partner Discussion Guide',
    body: 'A worksheet to help structure conversations about communication opportunities and support needs.',
  },
  {
    icon: 'tracker',
    title: 'Implementation Follow-Up Tracker',
    body: 'A documentation tool for recording observations, identifying questions, and organizing next steps.',
  },
] as const;

export const bonuses = [
  {
    number: 1,
    title: 'Everyday AAC Communication Board Collection',
    lead: 'Organize visual communication resources around everyday activities.',
    body: 'Explore sample layouts and editable planning sheets for activities such as meals, classroom participation, personal routines, and leisure.',
    value: 19,
    image: 'https://cdn.magicpatterns.com/uploads/9aWazdjgZ941HiqfbgPMrU/BONUS_1_AAC-EN.png',
  },
  {
    number: 2,
    title: 'Visual Routine Planning Toolkit',
    lead: 'Plan visual sequences for activities and transitions.',
    body: 'Includes routine planning sheets, sequence organizers, and examples for structuring visual supports in different environments.',
    value: 17,
    image: 'https://cdn.magicpatterns.com/uploads/i96az79Z2ZuxmqUHMmnyRe/BONUS_2_AAC-EN.png',
  },
  {
    number: 3,
    title: 'AAC Vocabulary Organization Workbook',
    lead: 'Create a structured starting point for vocabulary planning.',
    body: 'Includes vocabulary organization worksheets and examples covering everyday communication functions, preferences, and activity-specific language.',
    value: 17,
    image: 'https://cdn.magicpatterns.com/uploads/mWThPzML2PgMTRRx8aPYZk/BONUS_3_AAC-EN.png',
  },
  {
    number: 4,
    title: 'Communication Partner Training Workbook',
    lead: 'Organize discussions and learning activities for communication partners.',
    body: 'Includes reflection questions, discussion worksheets, and planning exercises for families, educators, and support teams.',
    value: 19,
    image: 'https://cdn.magicpatterns.com/uploads/bouxd9CJZe6NquUFL1Rq6V/BONUS_4_AAC-EN.png',
  },
  {
    number: 5,
    title: 'AAC Documentation & Follow-Up Pack',
    lead: 'Keep important planning information together.',
    body: 'Includes templates for recording observations, organizing follow-up questions, documenting adjustments, and preparing collaborative reviews.',
    value: 17,
    image: 'https://cdn.magicpatterns.com/uploads/hSkyE2Faoq2GVR2jNy3axA/BONUS_5_AAC-EN.png',
  },
];

export const valueStack = [
  { label: 'Volume 1 — AAC Foundations & Planning Framework', value: 19 },
  { label: 'Volume 2 — Functional Communication Observation Guide', value: 19 },
  { label: 'Volume 3 — AAC System Planning & Personalization', value: 19 },
  { label: 'Volume 4 — Visual Communication Resource Design', value: 19 },
  { label: 'Volume 5 — Communication Partner Collaboration', value: 19 },
  { label: 'Volume 6 — AAC Implementation & Follow-Up Planner', value: 19 },
  { label: 'Bonus 1 — Everyday AAC Communication Board Collection', value: 19 },
  { label: 'Bonus 2 — Visual Routine Planning Toolkit', value: 17 },
  { label: 'Bonus 3 — AAC Vocabulary Organization Workbook', value: 17 },
  { label: 'Bonus 4 — Communication Partner Training Workbook', value: 19 },
  { label: 'Bonus 5 — AAC Documentation & Follow-Up Pack', value: 17 },
];

export const testimonials = [
  { name: '[Customer name]', role: '[Role · Country]', photo: '', quote: '[INSERT VERIFIED CUSTOMER TESTIMONIAL]' },
  { name: '[Customer name]', role: '[Role · Country]', photo: '', quote: '[INSERT VERIFIED CUSTOMER TESTIMONIAL]' },
  { name: '[Customer name]', role: '[Role · Country]', photo: '', quote: '[INSERT VERIFIED CUSTOMER TESTIMONIAL]' },
  { name: '[Customer name]', role: '[Role · Country]', photo: '', quote: '[INSERT VERIFIED CUSTOMER TESTIMONIAL]' },
  { name: '[Customer name]', role: '[Role · Country]', photo: '', quote: '[INSERT VERIFIED CUSTOMER TESTIMONIAL]' },
];

export const faqs = [
  {
    q: 'What is the AAC Clinical Blueprint™?',
    a: 'It is a digital educational collection containing six volumes and five complementary resources focused on organizing AAC planning, visual communication resources, observation records, communication partner collaboration, and implementation documentation.',
  },
  {
    q: 'Who is this collection designed for?',
    a: 'It is intended for speech-language pathologists, special education professionals, educators, and other professionals involved in communication support who want structured educational planning resources.',
  },
  {
    q: 'Is this a clinical certification program?',
    a: 'No. It is an educational resource collection, not a certification program or a substitute for specialist education.',
  },
  {
    q: 'Can I adapt the templates to different settings?',
    a: 'The templates are intended as starting points that can be adapted to relevant contexts. Their suitability depends on the individual’s communication needs, preferences, access requirements, and the professional’s judgment.',
  },
  {
    q: 'How will I receive the materials?',
    a: '[CONFIRM DELIVERY METHOD AND ACCESS INSTRUCTIONS BEFORE PUBLISHING.]',
  },
  {
    q: 'Is this a recurring subscription?',
    a: 'The advertised price is a one-time payment of $27, not a recurring monthly fee.',
  },
  {
    q: 'Is the collection available outside the United States?',
    a: 'Digital availability will depend on the delivery platform and applicable payment options. [CONFIRM INTERNATIONAL AVAILABILITY.]',
  },
  {
    q: 'What is your refund policy?',
    a: '[INSERT THE ACTUAL REFUND POLICY BEFORE PUBLISHING.]',
  },
];

export const buyerNames = [
  'Valentina R.', 'Mateo G.', 'Camila S.', 'Santiago L.', 'Lucía M.', 'Sofía P.',
  'Diego F.', 'Isabella C.', 'Martina V.', 'Alejandro T.', 'Gabriela H.', 'Daniela O.',
  'Andrés Q.', 'Paula N.', 'Fernanda D.', 'Carolina B.', 'Javier E.', 'Natalia J.',
];

export const buyerCountries = [
  'Mexico', 'Argentina', 'Colombia', 'Chile', 'Peru', 'Spain', 'Uruguay', 'Ecuador',
  'Guatemala', 'Costa Rica', 'Bolivia', 'Paraguay', 'Dominican Republic', 'Panama',
  'Honduras', 'El Salvador', 'Puerto Rico', 'Venezuela',
];
